import Vue from "vue";
import { ZodError } from "zod";

import {
  NativeHealthCapability,
  NativeHealthState,
  parseNativeHealthActionAck,
  parseNativeHealthCapability,
  parseNativeHealthState
} from "@/utils/healthBridgeSchemas";
import { isHealthNativeBridgeAvailable, postHealthCommand } from "@/utils/healthNativeBridge";

const unavailableCapability = (): NativeHealthCapability => ({
  schemaVersion: 1,
  enabled: false,
  available: false,
  platform: "unknown",
  provider: "unavailable",
  supportsHistory: false,
  supportsBackground: false
});

const getInitialState = () => ({
  capability: unavailableCapability(),
  healthState: null as NativeHealthState | null,
  capabilityLoaded: false,
  loading: false,
  actionPending: false,
  error: null as string | null,
  listenersRegistered: false
});

const ownerKey = context => String(context.rootState.saude.vinculo || "");

const parseStateResponse = (context, response: unknown) => {
  if (response !== undefined && response !== null) {
    context.commit("SET_HEALTH_STATE", parseNativeHealthState(response));
  }
};

export default {
  namespaced: true,
  state: getInitialState,
  getters: {
    canAccess: state => state.capabilityLoaded && state.capability.enabled && state.capability.available,
    isOnboarded: state =>
      Boolean(
        state.healthState &&
          (state.healthState.authorization !== "not_determined" || state.healthState.status === "connected")
      )
  },
  actions: {
    registerListeners(context) {
      if (context.state.listenersRegistered || !globalThis.window.bus) return;

      globalThis.window.bus.$on("health:stateChanged", message => {
        try {
          context.commit("SET_HEALTH_STATE", parseNativeHealthState(message));
          context.commit("SET_ERROR", null);
        } catch (validationError) {
          if (validationError instanceof ZodError) {
            const issues = validationError.issues.map(issue => `${issue.path.join(".")}:${issue.code}`).join(", ");
            console.warn(`[health] stateChanged rejeitado pelo contrato: ${issues}`);
          }
          context.commit("SET_ERROR", "Não foi possível atualizar os dados de saúde.");
        }
      });
      globalThis.window.bus.$on("native:ready", () => context.dispatch("initializeHealth", true));
      context.commit("SET_LISTENERS_REGISTERED", true);
    },
    async ensureCapability(context, force = false) {
      context.dispatch("registerListeners");
      if (context.state.capabilityLoaded && !force) return context.getters.canAccess;
      if (!isHealthNativeBridgeAvailable()) {
        context.commit("SET_CAPABILITY", unavailableCapability());
        context.commit("SET_CAPABILITY_LOADED", true);
        return false;
      }

      try {
        const response = await Promise.resolve(postHealthCommand({ type: "health:getCapability" }));
        context.commit("SET_CAPABILITY", response ? parseNativeHealthCapability(response) : unavailableCapability());
      } catch {
        context.commit("SET_CAPABILITY", unavailableCapability());
      } finally {
        context.commit("SET_CAPABILITY_LOADED", true);
      }
      return context.getters.canAccess;
    },
    async initializeHealth(context, force = false) {
      const canAccess = await context.dispatch("ensureCapability", force);
      if (canAccess && ownerKey(context)) await context.dispatch("loadState");
      return canAccess;
    },
    async loadState(context) {
      if (context.state.loading) return;
      if (!(await context.dispatch("ensureCapability"))) return;
      const requestedOwner = ownerKey(context);
      if (!requestedOwner) return;
      context.commit("SET_LOADING", true);
      context.commit("SET_ERROR", null);
      try {
        const response = await Promise.resolve(
          postHealthCommand({ type: "health:getState", payload: { ownerKey: requestedOwner } })
        );
        if (ownerKey(context) === requestedOwner) parseStateResponse(context, response);
      } catch {
        if (ownerKey(context) === requestedOwner)
          context.commit("SET_ERROR", "Não foi possível carregar seus dados de saúde.");
      } finally {
        if (ownerKey(context) === requestedOwner) context.commit("SET_LOADING", false);
      }
    },
    async connectIfNeeded(context) {
      await context.dispatch("loadState");
      if (!context.getters.isOnboarded) await context.dispatch("requestAuthorization");
      return context.getters.isOnboarded;
    },
    async requestAuthorization(context) {
      return context.dispatch("runStateAction", "health:requestAuthorization");
    },
    async syncNow(context) {
      return context.dispatch("runStateAction", "health:syncNow");
    },
    async syncForRouteOpen(context) {
      context.commit("SET_ACTION_PENDING", true);
      try {
        const response = await Promise.resolve(
          postHealthCommand({
            type: "health:syncNow",
            payload: { ownerKey: ownerKey(context), reason: "route_open" }
          })
        );
        parseStateResponse(context, response);
      } finally {
        context.commit("SET_ACTION_PENDING", false);
      }
    },
    async runStateAction(context, type: "health:requestAuthorization" | "health:syncNow") {
      if (context.state.actionPending || (type === "health:syncNow" && context.state.healthState?.status === "syncing"))
        return;
      const requestedOwner = ownerKey(context);
      if (!requestedOwner) return;
      context.commit("SET_ACTION_PENDING", true);
      context.commit("SET_ERROR", null);
      try {
        const command =
          type === "health:syncNow"
            ? ({ type, payload: { ownerKey: requestedOwner, reason: "manual" as const } } as const)
            : ({ type, payload: { ownerKey: requestedOwner } } as const);
        const response = await Promise.resolve(postHealthCommand(command));
        if (ownerKey(context) === requestedOwner) parseStateResponse(context, response);
      } catch {
        if (ownerKey(context) === requestedOwner)
          context.commit("SET_ERROR", "A ação não pôde ser concluída. Tente novamente.");
      } finally {
        if (ownerKey(context) === requestedOwner) context.commit("SET_ACTION_PENDING", false);
      }
    },
    async openSettings() {
      await Promise.resolve(postHealthCommand({ type: "health:openSettings" }));
    },
    async disconnect(context) {
      try {
        const response = await Promise.resolve(
          postHealthCommand({ type: "health:disconnect", payload: { ownerKey: ownerKey(context) } })
        );
        const acknowledgement = response ? parseNativeHealthActionAck(response) : { ok: false };
        if (acknowledgement.ok) await context.dispatch("loadState");
        return acknowledgement.ok;
      } catch {
        context.commit("SET_ERROR", "Não foi possível desconectar a integração de saúde.");
        return false;
      }
    }
  },
  mutations: {
    SET_CAPABILITY: (state, value) => Vue.set(state, "capability", value),
    SET_HEALTH_STATE: (state, value) => Vue.set(state, "healthState", value),
    SET_CAPABILITY_LOADED: (state, value) => Vue.set(state, "capabilityLoaded", value),
    SET_LOADING: (state, value) => Vue.set(state, "loading", value),
    SET_ACTION_PENDING: (state, value) => Vue.set(state, "actionPending", value),
    SET_ERROR: (state, value) => Vue.set(state, "error", value),
    SET_LISTENERS_REGISTERED: (state, value) => Vue.set(state, "listenersRegistered", value),
    RESET_CONTEXT: state => {
      const listenersRegistered = state.listenersRegistered;
      Object.assign(state, getInitialState(), { listenersRegistered });
    },
    LOGOUT_USER: state => {
      const listenersRegistered = state.listenersRegistered;
      Object.assign(state, getInitialState(), { listenersRegistered });
    }
  }
};
